(async function () {
  const BONUS_IDS = [];

  const MIN_PAID_ITEMS_FOR_GIFT = 2;
  const MIN_SPEND_FOR_GIFT = 0;

  // Quando true: se o usuário remover manualmente um brinde, ele NÃO será re-adicionado.
  // O estado é persistido em cart attributes (_removed_gift_<id> = "true").
  const ALLOW_REMOVE_GIFT = true;

  const REMOVED_GIFT_ATTR_PREFIX = '_removed_gift_';
  const DEBOUNCE_MS = 300;

  let syncing = false;
  let debounceTimer = null;
  let lastCartState = null;
  let lastGiftIds = null; // Set com os IDs de brinde presentes no último sync

  // Generates simple hash from the cart to detect real time changes
  function getCartHash(cart) {
    const items = cart.items.map(i => `${i.id}:${i.quantity}:${i.properties?.discounted_item || ''}`).sort().join('|');
    return items;
  }

  // --- Helpers de Cart ---
  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw new Error('Failed to load cart');
    return await res.json();
  }

  function isGiftLineStrict(item) {
    return BONUS_IDS.includes(item.id) && item.properties?.discounted_item === 'true';
  }

  function countPaidItems(cart) {
    return cart.items.reduce((sum, it) => sum + (isGiftLineStrict(it) ? 0 : it.quantity), 0);
  }

  function calcPaidSpend(cart) {
    return cart.items.reduce((sum, it) => {
      if (isGiftLineStrict(it)) return sum;
      return sum + it.final_line_price; // already in cents, excludes discounts
    }, 0);
  }

  function getGiftLinesStrict(cart) {
    return cart.items.filter(isGiftLineStrict);
  }

  function meetsGiftConditions(cart) {
    const paidCount = countPaidItems(cart);
    const paidSpend = calcPaidSpend(cart);
    return (
      (MIN_PAID_ITEMS_FOR_GIFT && paidCount >= MIN_PAID_ITEMS_FOR_GIFT) ||
      (MIN_SPEND_FOR_GIFT && paidSpend >= MIN_SPEND_FOR_GIFT)
    );
  }

  // --- Helpers de remoção manual via cart attributes ---
  function getManuallyRemovedIds(cart) {
    const removed = new Set();
    const attrs = cart.attributes || {};
    for (const [key, value] of Object.entries(attrs)) {
      if (key.startsWith(REMOVED_GIFT_ATTR_PREFIX) && String(value) === 'true') {
        const id = parseInt(key.slice(REMOVED_GIFT_ATTR_PREFIX.length), 10);
        if (!Number.isNaN(id)) removed.add(id);
      }
    }
    return removed;
  }

  async function updateCartAttributes(attributes) {
    if (!attributes || !Object.keys(attributes).length) return;
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes }),
    });
  }

  async function markGiftsAsManuallyRemoved(ids) {
    if (!ids.length) return;
    console.log('🚫 Marking gifts as manually removed:', ids);
    const attributes = {};
    for (const id of ids) attributes[`${REMOVED_GIFT_ATTR_PREFIX}${id}`] = 'true';
    await updateCartAttributes(attributes);
  }

  async function clearManuallyRemovedFlags(cart) {
    const attrs = cart.attributes || {};
    const toClear = {};
    for (const key of Object.keys(attrs)) {
      if (key.startsWith(REMOVED_GIFT_ATTR_PREFIX)) {
        toClear[key] = ''; // Shopify remove o attribute quando o valor é string vazia
      }
    }
    if (!Object.keys(toClear).length) return false;
    console.log('🧹 Clearing manually-removed flags:', Object.keys(toClear));
    await updateCartAttributes(toClear);
    return true;
  }

  async function addGifts(missingBonusIds) {
    if (!missingBonusIds.length) return false;

    console.log('🎁 Adding gifts:', missingBonusIds);

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: missingBonusIds.map((id) => ({
          id,
          quantity: 1,
          properties: { discounted_item: 'true' },
        })),
      }),
    });

    if (!res.ok) throw new Error('Failed to add gifts');
    return true;
  }

  async function removeAllStrictGifts(cart) {
    const strict = getGiftLinesStrict(cart);
    if (!strict.length) return false;

    console.log('🗑️ Removing gifts:', strict.map(l => l.key));

    const updates = {};
    for (const line of strict) updates[line.key] = 0;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });
    return true;
  }

  async function splitExcessGiftQuantityIntoPaid(cart) {
    const strict = getGiftLinesStrict(cart);
    const tasks = [];

    for (const line of strict) {
      if (line.quantity > 1) {
        console.log('✂️ Splitting excess gift qty:', line.quantity);
        const extraQty = line.quantity - 1;

        tasks.push(
          Promise.all([
            fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ id: line.key, quantity: 1 }),
            }),
            fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ items: [{ id: line.id, quantity: extraQty }] }),
            }),
          ])
        );
      }
    }

    if (!tasks.length) return false;
    await Promise.all(tasks);
    return true;
  }

  async function normalizeGiftQuantities(cart) {
    const dirty = getGiftLinesStrict(cart).filter((l) => l.quantity !== 1);
    if (!dirty.length) return false;

    console.log('🔧 Normalizing gift quantities');

    const updates = {};
    for (const l of dirty) updates[l.key] = 1;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });
    return true;
  }

  async function syncBonusNow() {
    if (syncing) {
      console.log('⏳ Already syncing, skipping');
      return;
    }

    syncing = true;
    console.log('🔄 Sync started');

    try {
      const cart = await fetchCart();
      const currentHash = getCartHash(cart);

      if (currentHash === lastCartState) {
        console.log('✅ Cart unchanged, skipping');
        return;
      }

      lastCartState = currentHash;

      const paidCount = countPaidItems(cart);
      const paidSpend = calcPaidSpend(cart);
      const currentGiftIds = new Set(getGiftLinesStrict(cart).map((l) => l.id));

      console.log('📊 Paid items:', paidCount);
      console.log('📊 Paid spend:', paidSpend);

      let mutated = false;
      const conditionsMet = meetsGiftConditions(cart);

      // --- Detecção de remoção manual ---
      // Só faz sentido detectar remoção quando:
      //   1) ALLOW_REMOVE_GIFT está ativo;
      //   2) já temos um snapshot anterior (lastGiftIds);
      //   3) as condições para ter brinde ainda são atendidas
      //      (caso contrário, fomos NÓS que removemos, não o usuário).
      let removedByUser = [];
      if (ALLOW_REMOVE_GIFT && lastGiftIds && conditionsMet) {
        removedByUser = [...lastGiftIds].filter((id) => !currentGiftIds.has(id));
        if (removedByUser.length) {
          await markGiftsAsManuallyRemoved(removedByUser);
        }
      }

      if (conditionsMet) {
        // Filtra os brindes que o usuário removeu manualmente (se a feature estiver ligada).
        // IMPORTANTE: incluímos `removedByUser` aqui também, porque o objeto `cart` em
        // memória ainda não reflete os attributes que acabamos de gravar via API.
        // Sem isso, o brinde recém-removido seria re-adicionado nesta mesma sync.
        const blockedIds = ALLOW_REMOVE_GIFT ? getManuallyRemovedIds(cart) : new Set();
        for (const id of removedByUser) blockedIds.add(id);

        const presentStrictIds = new Set(getGiftLinesStrict(cart).map((l) => l.id));
        const missingIds = BONUS_IDS.filter(
          (id) => !presentStrictIds.has(id) && !blockedIds.has(id)
        );

        if (missingIds.length) {
          await addGifts(missingIds);
          mutated = true;
        }

        const mutatedSplit = await splitExcessGiftQuantityIntoPaid(cart);
        mutated = mutated || mutatedSplit;

        if (mutatedSplit) {
          await new Promise(resolve => setTimeout(resolve, 200));
          const cart2 = await fetchCart();
          const mutatedNorm = await normalizeGiftQuantities(cart2);
          mutated = mutated || mutatedNorm;
        }
      } else {
        // Carrinho saiu do mínimo: removemos os brindes E limpamos os flags de
        // remoção manual, para que numa próxima compra o brinde possa voltar.
        mutated = await removeAllStrictGifts(cart);
        if (ALLOW_REMOVE_GIFT) {
          const cleared = await clearManuallyRemovedFlags(cart);
          mutated = mutated || cleared;
        }
      }

      if (mutated) {
        lastCartState = null;
        // Atualiza o snapshot de brindes a partir do estado real pós-mutação
        try {
          const finalCart = await fetchCart();
          lastGiftIds = new Set(getGiftLinesStrict(finalCart).map((l) => l.id));
        } catch (_) {
          lastGiftIds = currentGiftIds;
        }

        if (window.CartJS && typeof CartJS.getCart === 'function') {
          await CartJS.getCart();
          reloadSection('v3-cart');
        }
      } else {
        lastGiftIds = currentGiftIds;
      }

      console.log('✅ Sync completed, mutated:', mutated);
    } catch (err) {
      console.error('❌ Sync error:', err);
      lastCartState = null;
    } finally {
      syncing = false;
    }
  }

  function syncBonusDebounced() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(syncBonusNow, DEBOUNCE_MS);
  }

  // Prevents multiple bindings
  $(document).off('cart.requestComplete', syncBonusDebounced);
  $(document).on('cart.requestComplete', syncBonusDebounced);

  // Inicial
  setTimeout(syncBonusNow, 300);
})();